import { useState, useEffect } from "react";
import {
  CheckCircle2, XCircle, Eye, ChevronLeft, ChevronRight,
  Users, Wallet, Clock, AlertCircle, Filter, X, Bookmark,
  Link2, ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { customerPaymentsApi, partiesApi } from "@/lib/unifiedApi";
import { logActivity } from "@/lib/activityLog";
import { useAuth } from "@/context/AuthContext";
import { format, subMonths, addMonths } from "date-fns";
import { th } from "date-fns/locale";

const fmt = (n) => Number(n || 0).toLocaleString("th-TH", { minimumFractionDigits: 2 });

function StatusBadge({ status }) {
  const map = {
    pending:   { label: "รอยืนยัน",  cls: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30" },
    confirmed: { label: "ยืนยันแล้ว", cls: "bg-green-500/20 text-green-300 border-green-500/30" },
    rejected:  { label: "ปฏิเสธ",    cls: "bg-red-500/20 text-red-300 border-red-500/30" },
  };
  const s = map[status] ?? map.pending;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${s.cls}`}>{s.label}</span>;
}

function PayTypeBadge({ type }) {
  return type === "deposit"
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-500/20 text-indigo-300 border border-indigo-500/30"><Bookmark size={11}/>มัดจำ</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"><CheckCircle2 size={11}/>ส่วนที่เหลือ</span>;
}

// ─── Detail modal ──────────────────────────────────────────
function PaymentDetailModal({ payment, open, onClose, onAction }) {
  const { isAdmin, user } = useAuth();
  const [acting, setActing] = useState(false);
  const [parties, setParties] = useState([]);
  const [partyMenu, setPartyMenu] = useState(false);
  const [partyQ, setPartyQ] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const unsub = partiesApi.subscribe((data) => {
      setParties(
        data
          .filter((p) => p.active !== false && (p.types || []).includes("customer"))
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "th"))
      );
    }, () => {});
    return unsub;
  }, [open]);

  async function linkParty(p) {
    try {
      await customerPaymentsApi.update(payment.id, { party_id: p.id, customer_name: p.name });
      logActivity(user?.id, user?.name, "payment_link", `฿${fmt(payment.amount)} → ${p.name}`);
      toast.success(`ผูกกับ ${p.name}`);
      setPartyMenu(false); setPartyQ("");
      onAction();
    } catch { toast.error("ผูกคู่ค้าไม่สำเร็จ"); }
  }

  async function unlinkParty() {
    try {
      await customerPaymentsApi.update(payment.id, { party_id: null });
      toast.success("ยกเลิกการผูกแล้ว");
      onAction();
    } catch { toast.error("ทำรายการไม่สำเร็จ"); }
  }

  async function doConfirm(type) {
    setActing(true);
    try {
      await customerPaymentsApi.confirm(payment.id, type);
      logActivity(user?.id, user?.name, "payment_confirm", `${payment.customer_name || payment.payer || ""} ฿${fmt(payment.amount)} (${type === "deposit" ? "มัดจำ" : "ส่วนที่เหลือ"})`);
      toast.success("ยืนยันแล้ว");
      onAction(); onClose();
    } catch { toast.error("เกิดข้อผิดพลาด"); }
    finally { setActing(false); }
  }

  async function doReject() {
    setActing(true);
    try {
      await customerPaymentsApi.reject(payment.id);
      logActivity(user?.id, user?.name, "payment_reject", `${payment.customer_name || payment.payer || ""} ฿${fmt(payment.amount)}`);
      toast.error("ปฏิเสธแล้ว");
      onAction(); onClose();
    } catch { toast.error("เกิดข้อผิดพลาด"); }
    finally { setActing(false); }
  }

  if (!payment) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={18} className="text-[#BFFF00]" />
            รายละเอียดการชำระเงิน
          </DialogTitle>
        </DialogHeader>

        {payment.slip_image_url && (
          <div className="rounded-xl overflow-hidden border border-white/10">
            <img src={payment.slip_image_url} alt="สลิป" className="w-full object-contain max-h-64" />
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-3xl font-bold text-[#BFFF00]">฿{fmt(payment.amount)}</span>
            <div className="flex flex-col items-end gap-1">
              <StatusBadge status={payment.status} />
              {payment.status === "confirmed" && <PayTypeBadge type={payment.payment_type} />}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {[
              ["ชื่อลูกค้า", payment.customer_name ?? payment.payer ?? "-"],
              ["วันที่", payment.slip_date ?? "-"],
              ["ผู้โอน", payment.payer ?? "-"],
              ["ผู้รับ", payment.payee ?? "-"],
              ["ธนาคารต้นทาง", payment.bank_from ?? "-"],
              ["ธนาคารปลายทาง", payment.bank_to ?? "-"],
              ["เลขอ้างอิง", payment.ref_no ?? "-"],
            ].map(([k, v]) => (
              <div key={k} className="bg-white/5 rounded-lg p-2">
                <p className="text-gray-400 text-xs">{k}</p>
                <p className="text-white text-sm font-medium mt-0.5 truncate" title={String(v)}>{v}</p>
              </div>
            ))}
          </div>

          {/* Link to a customer in the register (AR) */}
          {isAdmin() && (
            <div className="relative pt-1">
              <p className="text-xs text-gray-400 mb-1">คู่ค้า (สำหรับยอดค้างรับ)</p>
              {payment.party_id ? (
                <div className="flex items-center justify-between bg-white/5 rounded-lg p-2">
                  <span className="text-sm text-white flex items-center gap-1.5">
                    <Link2 size={13} className="text-[#BFFF00]" />
                    {parties.find((p) => p.id === payment.party_id)?.name || payment.customer_name || "ผูกแล้ว"}
                  </span>
                  <button onClick={unlinkParty} className="text-xs text-gray-400 hover:text-red-400">ยกเลิกผูก</button>
                </div>
              ) : (
                <>
                  <button type="button" onClick={() => setPartyMenu((v) => !v)}
                    className="w-full flex items-center justify-between bg-white/5 hover:bg-white/10 rounded-lg p-2 text-sm text-gray-400">
                    <span>เลือกลูกค้าจากทะเบียน…</span>
                    <ChevronDown size={15} />
                  </button>
                  {partyMenu && (
                    <div className="absolute z-30 mt-1 w-full rounded-lg bg-[#222] border border-white/15 shadow-xl max-h-56 overflow-hidden flex flex-col">
                      <div className="p-2 border-b border-white/10">
                        <input autoFocus value={partyQ} onChange={(e) => setPartyQ(e.target.value)}
                          placeholder="ค้นหาชื่อ / เลขภาษี"
                          className="w-full bg-black/50 border border-gray-700 rounded-md text-white text-sm p-1.5" />
                      </div>
                      <div className="overflow-y-auto">
                        {parties
                          .filter((p) => {
                            const q = partyQ.trim().toLowerCase();
                            if (!q) return true;
                            return (p.name || "").toLowerCase().includes(q) || (p.tax_id || "").includes(q);
                          })
                          .map((p) => (
                            <button key={p.id} type="button" onClick={() => linkParty(p)}
                              className="w-full text-left px-3 py-2 hover:bg-white/10 text-sm">
                              <div className="text-white">{p.name}</div>
                              <div className="text-gray-500 text-xs">{p.code}{p.tax_id ? ` · ${p.tax_id}` : ""}</div>
                            </button>
                          ))}
                        {parties.length === 0 && (
                          <div className="px-3 py-4 text-center text-gray-500 text-sm">ยังไม่มีคู่ค้าในทะเบียน</div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {isAdmin() && payment.status === "pending" && (
            <div className="space-y-2 pt-2">
              <p className="text-xs text-gray-400">เลือกประเภทการชำระ:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button onClick={() => doConfirm("deposit")} disabled={acting} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  <CheckCircle2 size={14} className="mr-1" /> มัดจำ
                </Button>
                <Button onClick={() => doConfirm("remaining")} disabled={acting} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle2 size={14} className="mr-1" /> ส่วนที่เหลือ
                </Button>
              </div>
              <Button onClick={doReject} disabled={acting} variant="outline" className="w-full border-red-500/30 text-red-400 bg-transparent hover:bg-red-500/10">
                <XCircle size={14} className="mr-1" /> ปฏิเสธ
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ─────────────────────────────────────────────
export default function CustomerPaymentsPage() {
  const { isAdmin, user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    setLoading(true);
    const unsub = customerPaymentsApi.subscribeAll((data) => {
      setPayments(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const monthStr = format(currentMonth, "yyyy-MM");
  const filtered = payments.filter((p) => {
    const inMonth  = !p.slip_date || p.slip_date.startsWith(monthStr);
    const inStatus = filterStatus === "all" || p.status === filterStatus;
    const inType   = filterType === "all" || p.payment_type === filterType;
    return inMonth && inStatus && inType;
  });

  const confirmed = filtered.filter((p) => p.status === "confirmed");
  const stats = {
    total:     confirmed.reduce((s, p) => s + Number(p.amount ?? 0), 0),
    pending:   filtered.filter((p) => p.status === "pending").length,
    deposits:  confirmed.filter((p) => p.payment_type === "deposit").reduce((s, p) => s + Number(p.amount ?? 0), 0),
    remaining: confirmed.filter((p) => p.payment_type === "remaining").reduce((s, p) => s + Number(p.amount ?? 0), 0),
  };

  return (
    <div className="pt-16 lg:pt-6 px-4 sm:px-6 pb-6 space-y-6 min-h-screen" style={{ backgroundColor: "#0a0a0a", color: "#fff" }}>
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Users size={24} className="text-[#BFFF00]" />
          การชำระเงินลูกค้า
        </h1>
        <p className="text-gray-400 text-sm mt-1">สลิปที่ลูกค้าส่งมาผ่าน LINE</p>
      </div>

      {/* Month nav */}
      <div className="flex items-center gap-3">
        <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 rounded-lg bg-white/5 hover:bg-white/10">
          <ChevronLeft size={16} className="text-gray-400" />
        </button>
        <span className="text-white font-semibold min-w-[120px] text-center">
          {format(currentMonth, "MMMM yyyy", { locale: th })}
        </span>
        <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 rounded-lg bg-white/5 hover:bg-white/10">
          <ChevronRight size={16} className="text-gray-400" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "ยอดรวม (ยืนยัน)", value: `฿${fmt(stats.total)}`, icon: <Wallet size={18}/>, accent: "#BFFF00" },
          { label: "รอยืนยัน", value: `${stats.pending} ราย`, icon: <Clock size={18}/>, accent: "#f59e0b" },
          { label: "มัดจำ", value: `฿${fmt(stats.deposits)}`, icon: <Bookmark size={18}/>, accent: "#a78bfa" },
          { label: "ส่วนที่เหลือ", value: `฿${fmt(stats.remaining)}`, icon: <CheckCircle2 size={18}/>, accent: "#34d399" },
        ].map((s) => (
          <div key={s.label} className="bg-[#1a1a1a] rounded-2xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-2" style={{ color: s.accent }}>
              {s.icon}
              <span className="text-xs font-medium text-gray-400">{s.label}</span>
            </div>
            <p className="text-xl font-bold text-white">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Filter size={14} className="text-gray-400" />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white text-sm">
            <SelectValue placeholder="ทุกสถานะ" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
            <SelectItem value="all">ทุกสถานะ</SelectItem>
            <SelectItem value="pending">รอยืนยัน</SelectItem>
            <SelectItem value="confirmed">ยืนยันแล้ว</SelectItem>
            <SelectItem value="rejected">ปฏิเสธ</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white text-sm">
            <SelectValue placeholder="ทุกประเภท" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
            <SelectItem value="all">ทุกประเภท</SelectItem>
            <SelectItem value="deposit">มัดจำ</SelectItem>
            <SelectItem value="remaining">ส่วนที่เหลือ</SelectItem>
          </SelectContent>
        </Select>
        {(filterStatus !== "all" || filterType !== "all") && (
          <button onClick={() => { setFilterStatus("all"); setFilterType("all"); }} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white">
            <X size={12} /> ล้างตัวกรอง
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-[#1a1a1a] rounded-2xl border border-white/5 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-[#BFFF00] mr-3" />
            กำลังโหลด...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <AlertCircle size={32} className="mb-3 opacity-40" />
            <p>ไม่มีรายการในเดือนนี้</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5">
                  {["วันที่", "ชื่อลูกค้า", "ยอดเงิน", "ประเภท", "ธนาคาร", "สถานะ", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{p.slip_date ?? "-"}</td>
                    <td className="px-4 py-3 text-white font-medium">{p.customer_name ?? p.payer ?? "-"}</td>
                    <td className="px-4 py-3 font-semibold text-white whitespace-nowrap">฿{fmt(p.amount)}</td>
                    <td className="px-4 py-3">
                      {p.status === "confirmed"
                        ? <PayTypeBadge type={p.payment_type} />
                        : <span className="text-gray-500 text-xs">-</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {[p.bank_from, p.bank_to].filter(Boolean).join(" → ") || "-"}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setDetail(p)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white" title="ดูรายละเอียด">
                          <Eye size={14} />
                        </button>
                        {isAdmin() && p.status === "pending" && (
                          <>
                            <button onClick={async () => { await customerPaymentsApi.confirm(p.id, "deposit"); logActivity(user?.id, user?.name, "payment_confirm", `${p.customer_name || p.payer || ""} ฿${fmt(p.amount)} (มัดจำ)`); toast.success("ยืนยัน (มัดจำ)"); }} className="p-1.5 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400" title="มัดจำ">
                              <Bookmark size={14}/>
                            </button>
                            <button onClick={async () => { await customerPaymentsApi.confirm(p.id, "remaining"); logActivity(user?.id, user?.name, "payment_confirm", `${p.customer_name || p.payer || ""} ฿${fmt(p.amount)} (ส่วนที่เหลือ)`); toast.success("ยืนยัน (ส่วนที่เหลือ)"); }} className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400" title="ส่วนที่เหลือ">
                              <CheckCircle2 size={14} />
                            </button>
                            <button onClick={async () => { await customerPaymentsApi.reject(p.id); logActivity(user?.id, user?.name, "payment_reject", `${p.customer_name || p.payer || ""} ฿${fmt(p.amount)}`); toast.error("ปฏิเสธแล้ว"); }} className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400" title="ปฏิเสธ">
                              <XCircle size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-white/5 text-xs text-gray-500">
            {filtered.length} รายการ · ยืนยันแล้ว {confirmed.length} รายการ
          </div>
        )}
      </div>

      <PaymentDetailModal open={!!detail} onClose={() => setDetail(null)} payment={detail} onAction={() => {}} />
    </div>
  );
}
